import React from 'react';
import { Plus, X } from 'lucide-react';
import MoneyInput from '../shared/MoneyInput';
import NumberInput from '../shared/NumberInput';
import PickSelect from '../shared/PickSelect';
import {
  BAKEOFF_BUSINESS_RETURNS, BAKEOFF_K1_FIELDS, BAKEOFF_ROYALTY_FIELDS,
} from './constants';

const CELL = 'w-full mt-1 px-2 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-brand-navy';
const LABEL = 'text-[10px] uppercase tracking-wide text-slate-500 font-semibold';

// One labelled number box. `miles` (a count, not dollars) and `percent` use the plain number input.
export function NumIn({ label, miles, plain, value, onChange, testId }) {
  const Input = miles || plain ? NumberInput : MoneyInput;
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <Input value={value ?? null} onChange={onChange} placeholder="" data-testid={testId} className={CELL} />
    </div>
  );
}

const K1_TYPES = [
  { value: 'partnership', label: 'Partnership' },
  { value: 's_corp', label: 'S-Corp' },
];
const BIZ_OPTIONS = [
  { value: 'none', label: 'None' },
  ...Object.entries(BAKEOFF_BUSINESS_RETURNS).map(([value, r]) => ({ value, label: r.label })),
];
export const MAX_K1S = 5;

// The non-Schedule-C inputs of one bakeoff year: W-2 from the borrower's own
// business, Schedule E royalties, K-1s, and a business return's adjustments.
// `extras` is { k1s: [], biz: { type, values } }; scalar lines live in `inputs`.
export function YearExtras({ prefix, inputs, setInput, extras, setExtras }) {
  const { k1s, biz } = extras;
  const setK1 = (i, patch) => setExtras({ ...extras, k1s: k1s.map((k, j) => (j === i ? { ...k, ...patch } : k)) });
  const bizDef = BAKEOFF_BUSINESS_RETURNS[biz.type];
  return (
    <div className="space-y-3 pt-3 mt-3 border-t border-slate-200" data-testid={`compare-extras-${prefix}`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <NumIn
          label="W-2 From Own Business"
          value={inputs[`${prefix}_w2_self_employed_income`]}
          onChange={(n) => setInput(`${prefix}_w2_self_employed_income`, n)}
          testId={`compare-${prefix}-w2_self_employed_income`}
        />
        {BAKEOFF_ROYALTY_FIELDS.map((f) => (
          <NumIn
            key={f.field}
            label={f.label}
            value={inputs[`${prefix}_${f.field}`]}
            onChange={(n) => setInput(`${prefix}_${f.field}`, n)}
            testId={`compare-${prefix}-${f.field}`}
          />
        ))}
      </div>

      <div className="space-y-2">
        {k1s.map((k, i) => (
          <div key={i} className="rounded-md border border-slate-200 bg-white p-2" data-testid={`compare-${prefix}-k1-${i}`}>
            <div className="flex items-end gap-3">
              <div className="w-36">
                <PickSelect
                  label={`K-1 ${i + 1}`}
                  value={k.entity_type}
                  onChange={(v) => setK1(i, { entity_type: v })}
                  options={K1_TYPES}
                  testId={`compare-${prefix}-k1-${i}-entity_type`}
                />
              </div>
              {BAKEOFF_K1_FIELDS.filter((f) => !f.partnershipOnly || k.entity_type === 'partnership').map((f) => (
                <div key={f.field} className="flex-1">
                  <NumIn
                    label={f.label}
                    value={k[f.field]}
                    onChange={(n) => setK1(i, { [f.field]: n })}
                    testId={`compare-${prefix}-k1-${i}-${f.field}`}
                  />
                </div>
              ))}
              <button
                type="button"
                onClick={() => setExtras({ ...extras, k1s: k1s.filter((_, j) => j !== i) })}
                className="p-1.5 hover:bg-red-50 rounded-lg mb-0.5"
                aria-label={`Remove K-1 ${i + 1}`}
                data-testid={`compare-${prefix}-k1-${i}-remove`}
              ><X size={14} className="text-red-500" /></button>
            </div>
          </div>
        ))}
        {k1s.length < MAX_K1S && (
          <button
            type="button"
            onClick={() => setExtras({ ...extras, k1s: [...k1s, { entity_type: 'partnership' }] })}
            className="inline-flex items-center gap-1 text-xs font-semibold text-brand-navy hover:underline"
            data-testid={`compare-${prefix}-k1-add`}
          ><Plus size={12} /> Add a K-1</button>
        )}
      </div>

      <div className="space-y-2">
        <div className="w-56">
          <PickSelect
            label="Business return adjustments"
            value={biz.type}
            onChange={(v) => setExtras({ ...extras, biz: { type: v, values: {} } })}
            options={BIZ_OPTIONS}
            testId={`compare-${prefix}-biz-type`}
          />
        </div>
        {bizDef && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`compare-${prefix}-biz`}>
            <NumIn
              plain
              label="Ownership % (blank = 100)"
              value={biz.values.ownership_pct}
              onChange={(n) => setExtras({ ...extras, biz: { ...biz, values: { ...biz.values, ownership_pct: n } } })}
              testId={`compare-${prefix}-biz-ownership_pct`}
            />
            {bizDef.fields.map((f) => (
              <NumIn
                key={f.field}
                label={f.label}
                value={biz.values[f.field]}
                onChange={(n) => setExtras({ ...extras, biz: { ...biz, values: { ...biz.values, [f.field]: n } } })}
                testId={`compare-${prefix}-biz-${f.field}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
